import _ from 'lodash';
import type { ComponentType, ReactNode } from 'react';
import type { FormulaNode, UiFormulaNode } from '../typeDefs';

export const getRandomHash = (size: number = 8): string =>
    Math.floor(Math.abs(10000 + Math.random() * 1000 * 1000 * 1000)).toString(32).slice(-1 * size);

const ESCAPE_REG_REG = /[\-\[\]\/\{\}\(\)\*\+\?\.\\\^\$\|]/g;
export const escapeRegExp = (str: string): string => str.replace(ESCAPE_REG_REG, '\\$&');

/** An expression component: a React component that also exposes a `getStub` factory
 *  and, optionally, a `renderActions` static and a `KEY` discriminator. */
export type ExpressionComponent = ComponentType<any> & {
    getStub: (args?: unknown[]) => FormulaNode;
    renderActions?: (data: UiFormulaNode) => ReactNode;
    KEY?: string | null;
};

export const getExpression = (key: string): ExpressionComponent | null => {
    switch (key) {
        case 'fn':              return require('../expressions/FnExpression').default;
        case 'and':             return require('../expressions/ListExpression').AndExpression;
        case 'or':              return require('../expressions/ListExpression').OrExpression;
        case 'if':              return require('../expressions/IfExpression').default;
        case 'for':             return require('../expressions/ForExpression').default;
        case 'ref':             return require('../expressions/RefExpression').default;
        case 'call':            return require('../expressions/CallExpression').default;
        case 'checker-message': return require('../expressions/CheckerExpression').CheckerMessage;
        case 'checker-list':    return require('../expressions/CheckerExpression').CheckerList;
        case 'checker-table':   return require('../expressions/CheckerExpression').CheckerTable;
        case 'checker-var':     return require('../expressions/CheckerExpression').CheckerVar;
    }
    return null;
};

export const getRandomName: () => string = (() => {
    const nameWords = [
        [
            'angry',
            'black',
            'blue',
            'clever',
            'cyan',
            'flamboyant',
            'fluffy',
            'green',
            'happy',
            'magenta',
            'mean',
            'orange',
            'purple',
            'red',
            'rotund',
            'sad',
            'senior',
            'serious',
            'white',
            'yellow',
        ],
        [
            'allergy',
            'aubergine',
            'avocado',
            'banana',
            'blueberry',
            'blues',
            'canada',
            'cantaloupe',
            'dudley',
            'granola',
            'mango',
            'panda',
            'papaya',
            'pizza',
        ],
    ];

    return () => nameWords.map(arr => _.sample(arr)).join('_') + '_' + Math.floor(Math.random() * 100);
})();

export const parseQuery = (): Record<string, string> =>
    window.location.search.slice(1)
        .split('&')
        .filter(str => str.trim().length)
        .map(str => str.trim().split('='))
        .reduce((obj: Record<string, string>, arr) => {
            obj[arr[0]] = decodeURIComponent(arr[1]);
            return obj;
        }, {});

export const deepSelect = (obj: unknown, props: string[]): Record<string, unknown> => {
    const result: Record<string, unknown> = {};
    for (let key of props) {
        result[key] = _.get(obj, key);
    }
    return result;
};

export const setIn = <T>(
    root: T,
    path: string | number | Array<string | number> | null | undefined,
    value: unknown,
): T => {
    if (!path) return root;

    const arr = _.toPath(path);
    const check = (obj: unknown): obj is Record<string, unknown> =>
        obj != null && typeof obj === 'object';
    const clone = (obj: unknown): unknown =>
        !check(obj) ? obj : Array.isArray(obj) ? Array.from(obj) : { ...obj };

    if (!check(root)) return root;
    const result = clone(root) as Record<string, unknown>;
    let parent: Record<string, unknown> = result;
    for (let index = 0; index < arr.length - 1; ++index) {
        if (!check(parent)) return root;
        const key = arr[index];
        parent[key] = clone(parent[key]);
        parent = parent[key] as Record<string, unknown>;
    }
    if (!check(parent)) return root;
    parent[arr[arr.length - 1]] = value;
    return result as unknown as T;
};

interface PrettyProductInput {
    placeList?: Array<string | { type: string; description: string }>;
    topology?: Array<{ category: string; subCategory?: string }>;
    computeOperations?: Array<{ type: string; prop: string; value: unknown }>;
    [key: string]: unknown;
}

export const prettyProduct = (obj: PrettyProductInput): Record<string, unknown> => {
    const result: Record<string, unknown> = { ...obj };

    if (Array.isArray(result.placeList)) {
        result.placeList = (result.placeList as PrettyProductInput['placeList'])!.map(item =>
            typeof item === 'string' ? item : `${item.type}: ${item.description}`,
        );
    }

    if (Array.isArray(result.topology)) {
        result.topology = (result.topology as PrettyProductInput['topology'])!
            .map(({ category, subCategory }) =>
                !subCategory ? category : `${category} - ${subCategory}`,
            )
            .sort();
    }

    for (let key of ['badgeList', 'dietList', 'certificationList', 'fdaBadgeList']) {
        const list = result[key];
        if (!Array.isArray(list)) continue;
        result[key] = list.map((item: { name: string }) => item.name);
    }

    if (Array.isArray(result.computeOperations)) {
        result.computeOperations = (result.computeOperations as PrettyProductInput['computeOperations'])!.map(
            ({ type, prop, value }) => [
                type,
                prop,
                typeof value === 'object' && value !== null ? (value as { name: unknown }).name : value,
            ],
        );
    }

    return result;
};

interface ErrorMessage {
    debug?: Array<string | { stack?: string }>;
    errors?: string[];
    stack?: string;
    toString(): string;
}

export const errorHandler = (
    message?: unknown,
    _file?: string,
    _row?: number,
    _col?: number,
    ex?: { stack?: string; message?: string; toString(): string },
): void => {
    const $ = (sel: string) => document.querySelector<HTMLElement>(sel);

    let container = $('#error');
    if (!container) {
        container = document.createElement('div');
        container.setAttribute('id', 'error');
        document.body.insertBefore(container, document.body.firstChild);
    }
    if (!message) {
        $('#error')?.remove();
        return;
    }
    let errorStr: string | null = null;
    const isObject = message && typeof message === 'object';
    const msg = message as ErrorMessage;
    if (isObject && msg.debug) errorStr = msg.debug.map(obj => (typeof obj === 'string' ? obj : obj.stack)).join('\n\n');
    else if (isObject && msg.errors) errorStr = msg.errors[0];
    else if (ex) errorStr = ex.stack || ex.message || ex.toString();
    else if (msg.errors) errorStr = msg.errors.join('\n');
    else if (msg.stack) errorStr = msg.stack;
    else errorStr = typeof message === 'object' ? JSON.stringify(message, null, 4) : String(message);

    const stack = (ex && ex.stack) || (isObject && msg.stack);
    if (stack) console.warn(stack);

    const el = $('#error');
    if (!el) return;
    el.innerHTML = errorStr;
    el.style.display = 'block';
    (el as HTMLElement & { scrollIntoViewIfNeeded?: () => void }).scrollIntoViewIfNeeded?.();
};
